import React, { useCallback, useEffect, useRef, useState } from 'react';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useEditor, useValue } from 'tldraw';
import { useBoardActivities } from '../context/BoardActivitiesContext';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import { resolveDisplayTitle } from '../utils/pinTitle';
import {
  ARROW_LABEL_MAX,
  arrowLabelMeta,
  readArrowLabel,
} from '../utils/arrowTerminalLabels';
import { getArrowTerminalPagePoints } from '../utils/arrowTerminalLabelGeometry';
import { getSelectedLinkArrowId } from '../utils/arrowSelectConnected';
import { reverseArrowConnection } from '../utils/reverseArrowConnection';

const PANEL_W = 300;
const PANEL_H = 112;
const VIEW_PAD = 12;
const ENDPOINT_PAD = 52;
const MID_HANDLE_PAD = 44;
const LINE_CORRIDOR_PAD = 28;

function isInteractingWithArrow(editor, arrowId) {
  const path = String(editor.getPath?.() || '');
  // Hide while grabbing handles or translating the line so the panel never
  // sits under the drag gesture.
  if (
    !path.includes('dragging_handle')
    && !path.includes('pointing_handle')
    && !path.includes('translating')
  ) {
    return false;
  }
  return getSelectedLinkArrowId(editor) === arrowId;
}

function arrowTerminalScreenZones(editor, arrowId) {
  const terminals = getArrowTerminalPagePoints(editor, arrowId);
  if (!terminals) return [];

  return [terminals.startPage, terminals.endPage].map(page => {
    const screen = editor.pageToScreen(page);
    return {
      x: screen.x - ENDPOINT_PAD,
      y: screen.y - ENDPOINT_PAD,
      w: ENDPOINT_PAD * 2,
      h: ENDPOINT_PAD * 2,
    };
  });
}

function arrowMidAndPerpendicular(editor, arrowId) {
  const terminals = getArrowTerminalPagePoints(editor, arrowId);
  if (!terminals) return null;

  const startScreen = editor.pageToScreen(terminals.startPage);
  const endScreen = editor.pageToScreen(terminals.endPage);
  const midScreen = terminals.midPage
    ? editor.pageToScreen(terminals.midPage)
    : {
      x: (startScreen.x + endScreen.x) / 2,
      y: (startScreen.y + endScreen.y) / 2,
    };

  const dx = endScreen.x - startScreen.x;
  const dy = endScreen.y - startScreen.y;
  const len = Math.hypot(dx, dy) || 1;
  const along = { x: dx / len, y: dy / len };
  const perp = { x: -dy / len, y: dx / len };

  return { midScreen, perp, along, startScreen, endScreen, len };
}

function lineCorridorZone(geometry) {
  if (!geometry) return null;
  const { startScreen, endScreen } = geometry;
  const minX = Math.min(startScreen.x, endScreen.x) - LINE_CORRIDOR_PAD;
  const maxX = Math.max(startScreen.x, endScreen.x) + LINE_CORRIDOR_PAD;
  const minY = Math.min(startScreen.y, endScreen.y) - LINE_CORRIDOR_PAD;
  const maxY = Math.max(startScreen.y, endScreen.y) + LINE_CORRIDOR_PAD;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function midHandleZone(geometry) {
  if (!geometry) return null;
  const { midScreen } = geometry;
  return {
    x: midScreen.x - MID_HANDLE_PAD,
    y: midScreen.y - MID_HANDLE_PAD,
    w: MID_HANDLE_PAD * 2,
    h: MID_HANDLE_PAD * 2,
  };
}

function truncateLabel(value, max = 28) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function labelForBoundShape(editor, shapeId, activityById) {
  const shape = editor.getShape(shapeId);
  if (!shape) return null;

  if (shape.type === 'pin-card') {
    const activityId = shape.meta?.activityId;
    const activity = activityId ? activityById?.(activityId) : null;
    const title = resolveDisplayTitle(
      activity?.title || activity?.action?.title || '',
      activity?.url || activity?.action?.url || '',
      { snippet: activity?.snippet, fallback: 'Pin' }
    );
    return truncateLabel(title);
  }

  if (shape.type === 'suggest-pin' || shape.type === 'conversation-pin') {
    const title = shape.props?.title || shape.meta?.title || 'AI pin';
    return truncateLabel(title);
  }

  if (shape.type === 'text' || shape.type === 'note') {
    return truncateLabel(shape.props?.text || shape.type);
  }

  if (shape.type === 'geo' || shape.type === 'frame') {
    return truncateLabel(shape.props?.text || shape.props?.name || shape.type);
  }

  return truncateLabel(shape.type);
}

function readArrowBindingState(editor, arrowId, activityById) {
  const shape = editor.getShape(arrowId);
  if (!shape || shape.type !== 'arrow') return null;

  const bindings = editor.getBindingsFromShape?.(arrowId, 'arrow') || [];
  let startId = null;
  let endId = null;
  for (const binding of bindings) {
    const terminal = binding?.props?.terminal;
    if (terminal === 'start') startId = binding.toId;
    if (terminal === 'end') endId = binding.toId;
  }

  const startTargetLabel = startId ? labelForBoundShape(editor, startId, activityById) : null;
  const endTargetLabel = endId ? labelForBoundShape(editor, endId, activityById) : null;

  return {
    startBound: Boolean(startId),
    endBound: Boolean(endId),
    startTargetLabel,
    endTargetLabel,
    label: readArrowLabel(shape),
    bothBound: Boolean(startId && endId),
  };
}

function overlapArea(a, b) {
  const x = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const y = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return x * y;
}

/**
 * Place the floating panel near the arrow midpoint, offset perpendicular to the
 * connector so start/mid/end handles stay free — never park in a viewport corner.
 */
function findClearScreenPos(editor, arrowId, panelW, panelH) {
  const arrowBounds = editor.getShapePageBounds(arrowId);
  if (!arrowBounds) return null;

  // Keep camera in the reactive graph.
  editor.getCamera?.();

  const geometry = arrowMidAndPerpendicular(editor, arrowId);
  const clearZones = [
    ...arrowTerminalScreenZones(editor, arrowId),
    midHandleZone(geometry),
    lineCorridorZone(geometry),
  ].filter(Boolean);

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  const midScreen = geometry?.midScreen || editor.pageToScreen({
    x: arrowBounds.x + arrowBounds.w / 2,
    y: arrowBounds.y + arrowBounds.h / 2,
  });
  const perp = geometry?.perp || { x: 0, y: -1 };
  const along = geometry?.along || { x: 1, y: 0 };

  // Prefer a modest offset off the stroke — close enough to feel attached,
  // far enough that mid/end handles stay grabbable.
  const rawCandidates = [];
  for (const side of [-1, 1]) {
    for (const dist of [64, 92, 124, 160]) {
      for (const slide of [0, -36, 36, -72, 72]) {
        rawCandidates.push({
          x: midScreen.x + perp.x * dist * side + along.x * slide - panelW / 2,
          y: midScreen.y + perp.y * dist * side + along.y * slide - panelH / 2,
        });
      }
    }
  }

  // Axis-aligned backups still anchored to the midpoint (no screen corners).
  for (const dist of [72, 110, 150]) {
    rawCandidates.push(
      { x: midScreen.x - panelW / 2, y: midScreen.y - panelH - dist },
      { x: midScreen.x - panelW / 2, y: midScreen.y + dist },
      { x: midScreen.x + dist, y: midScreen.y - panelH / 2 },
      { x: midScreen.x - panelW - dist, y: midScreen.y - panelH / 2 },
    );
  }

  let best = null;
  let bestScore = Infinity;

  rawCandidates.forEach((ideal, index) => {
    const x = Math.max(VIEW_PAD, Math.min(ideal.x, vw - panelW - VIEW_PAD));
    const y = Math.max(VIEW_PAD, Math.min(ideal.y, vh - panelH - VIEW_PAD));
    const panel = { x, y, w: panelW, h: panelH };
    const panelCenter = { x: x + panelW / 2, y: y + panelH / 2 };

    // Stay near the line; earlier candidates are preferred offsets.
    let score = index * 2;
    score += Math.hypot(panelCenter.x - midScreen.x, panelCenter.y - midScreen.y) * 0.35;
    score += Math.hypot(x - ideal.x, y - ideal.y) * 1.2;

    for (const zone of clearZones) {
      const area = overlapArea(panel, zone);
      if (area > 0) score += area + 50000;
    }

    if (score < bestScore) {
      bestScore = score;
      best = { x, y };
    }
  });

  return best;
}

/**
 * When a single arrow is selected: direction, one relationship label,
 * and a one-click reverse. Positioned to avoid overlapping the line.
 */
export default function ArrowBindingHint() {
  const editor = useEditor();
  const { activityById } = useBoardActivities();
  const { geminiLanguage } = useGeminiLanguage();
  const messages = getCanvasMessages(geminiLanguage);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const draftRef = useRef('');
  const editingRef = useRef(false);
  const [draft, setDraft] = useState('');

  const syncDraft = useCallback(value => {
    const next = String(value ?? '').slice(0, ARROW_LABEL_MAX);
    draftRef.current = next;
    setDraft(next);
  }, []);

  const focusStaysInPanel = useCallback(relatedTarget => {
    if (!relatedTarget || !(relatedTarget instanceof Node)) return false;
    return Boolean(panelRef.current?.contains(relatedTarget));
  }, []);

  const selectedArrowId = useValue(
    'arrow-binding-selected',
    () => getSelectedLinkArrowId(editor),
    [editor]
  );

  const binding = useValue(
    'arrow-binding-state',
    () => {
      if (!selectedArrowId) return null;
      return readArrowBindingState(editor, selectedArrowId, activityById);
    },
    [editor, selectedArrowId, activityById]
  );

  const isInteracting = useValue(
    'arrow-binding-dragging',
    () => {
      if (!selectedArrowId) return false;
      return isInteractingWithArrow(editor, selectedArrowId);
    },
    [editor, selectedArrowId]
  );

  const screenPos = useValue(
    'arrow-binding-screen-pos',
    () => {
      if (!selectedArrowId) return null;
      return findClearScreenPos(editor, selectedArrowId, PANEL_W, PANEL_H);
    },
    [editor, selectedArrowId]
  );

  useEffect(() => {
    if (editingRef.current) return;
    syncDraft(binding?.label || '');
  }, [selectedArrowId, binding?.label, syncDraft]);

  const commitLabel = useCallback(
    (nextRaw, { trim = true, history = 'record' } = {}) => {
      if (!selectedArrowId) return;
      const shape = editor.getShape(selectedArrowId);
      if (!shape || shape.type !== 'arrow') return;
      if (editor.getIsReadonly?.()) return;

      const raw = String(nextRaw ?? '').slice(0, ARROW_LABEL_MAX);
      const next = trim ? raw.trim() : raw;
      const prevStored = trim
        ? readArrowLabel(shape)
        : String(shape.meta?.label || shape.meta?.endLabel || shape.meta?.startLabel || '')
          .slice(0, ARROW_LABEL_MAX);

      if (next === prevStored) {
        if (trim) syncDraft(readArrowLabel(shape));
        return;
      }

      const runOpts = history === 'ignore' ? { history: 'ignore' } : undefined;
      editor.run(() => {
        editor.updateShape({
          id: selectedArrowId,
          type: 'arrow',
          meta: arrowLabelMeta(shape.meta, next),
          props: {
            ...shape.props,
            arrowheadStart: 'none',
            arrowheadEnd: 'arrow',
            text: '',
          },
        });
      }, runOpts);

      if (trim) syncDraft(next);
    },
    [editor, selectedArrowId, syncDraft]
  );

  const handleFieldBlur = useCallback(
    event => {
      editingRef.current = false;
      if (focusStaysInPanel(event.relatedTarget)) return;
      commitLabel(event.target.value, { trim: true, history: 'record' });
    },
    [commitLabel, focusStaysInPanel]
  );

  const reverseConnection = useCallback(() => {
    if (!selectedArrowId || editor.getIsReadonly?.()) return;
    editor.markHistoryStoppingPoint?.('reverse-connection');
    reverseArrowConnection(editor, selectedArrowId);
  }, [editor, selectedArrowId]);

  if (!binding || !screenPos || !selectedArrowId || isInteracting) return null;

  const startName = binding.startBound
    ? (binding.startTargetLabel || messages.arrowBoundEnd)
    : messages.arrowUnboundEnd;
  const endName = binding.endBound
    ? (binding.endTargetLabel || messages.arrowBoundEnd)
    : messages.arrowUnboundEnd;
  const readonly = Boolean(editor.getIsReadonly?.());

  return (
    <div
      ref={panelRef}
      className="board-arrow-menu"
      onPointerDown={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      style={{ left: screenPos.x, top: screenPos.y, width: PANEL_W }}
    >
      <div className="board-arrow-menu__direction">
        <span className="board-arrow-menu__name" title={startName}>{startName}</span>
        <span className="board-arrow-menu__glyph" aria-hidden>→</span>
        <span className="board-arrow-menu__name" title={endName}>{endName}</span>
        <button
          type="button"
          className="board-arrow-menu__reverse"
          aria-label={messages.arrowReverseAria}
          disabled={readonly}
          onMouseDown={event => event.preventDefault()}
          onClick={reverseConnection}
        >
          <SwapHorizIcon sx={{ fontSize: 16 }} />
          <span>{messages.arrowReverse}</span>
        </button>
      </div>

      {!binding.bothBound ? (
        <p className="board-arrow-menu__hint">
          {binding.startBound || binding.endBound
            ? messages.arrowBoundPartial
            : messages.arrowBoundHint}
        </p>
      ) : null}

      <input
        ref={inputRef}
        className="board-arrow-menu__label"
        value={draft}
        placeholder={messages.arrowLabelPlaceholder}
        maxLength={ARROW_LABEL_MAX}
        aria-label={messages.arrowLabelAria}
        disabled={readonly}
        onFocus={() => {
          editingRef.current = true;
        }}
        onChange={event => {
          const value = event.target.value;
          syncDraft(value);
          commitLabel(value, { trim: false, history: 'ignore' });
        }}
        onBlur={handleFieldBlur}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            syncDraft(event.currentTarget.value);
            commitLabel(event.currentTarget.value, { trim: true, history: 'record' });
            inputRef.current?.blur();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            syncDraft(binding.label || '');
            inputRef.current?.blur();
          } else {
            event.stopPropagation();
          }
        }}
      />
    </div>
  );
}
